<script setup lang="ts">
    import type { Schemas } from '#shopware';
    import { ApiClientError } from '@shopware/api-client';
    import { getProductRoute } from '#imports';

    type LineItem = Schemas['LineItem'];

    const {
        cartItem,
        maxQty = 100,
        isCheckout = false,
        isSideCart = false
    } = defineProps<{
        cartItem: LineItem;
        maxQty?: number;
        isCheckout?: boolean;
        isSideCart?: boolean;
    }>();

    const cartItemRef = computed(() => cartItem);

    const { getErrorsCodes } = useCartNotification();
    const { refreshCart } = useCart();
    const { hfpPushError } = useNotificationHelpers();
    // Blocking checkout is central; this only locks the item's own controls.
    const { isWritingToShopware } = useShopwareWriteState();

    const isRemoving = ref(false);

    const { t } = useI18n();
    const { urlSegment } = useLanguage();
    const { getStoryblokProductLinks } = useStoryblokProductsCache();
    const storyblokProductStoriesKey = computed(() => `storyblokProductStories-${urlSegment.value}`);
    const { data: productLinks, error } = await useAsyncData(storyblokProductStoriesKey, getStoryblokProductLinks);

    if (error.value) {
        throw createError({ status: 500, message: 'Error fetching product Stories', fatal: true });
    }

    const seoLink = computed(() => productLinks.value?.find((e) => e.id === cartItem.id) || null);

    const { itemOptions, removeItem, itemStock, itemQuantity, itemTotalPrice, isPromotion, changeItemQuantity } =
        useCartItem(cartItemRef);

    const listPrice = ref();

    if (cartItem.price?.listPrice?.discount !== 0) {
        const discountString = computed<string>(() => useFormatPrice(cartItem.price?.listPrice?.price));
        listPrice.value = discountString.value;
    }

    const { resolveApiErrors } = useApiErrorsResolver('account_login');

    // The debounce and write ordering live in `useCartItemQuantity`; this supplies
    // the request and the messages that follow it.
    const {
        quantity,
        isUpdating,
        isBusy: isQuantityEditPending
    } = useCartItemQuantity({
        itemQuantity,
        commitQuantity: async (newQuantity) => {
            try {
                const response = await changeItemQuantity(newQuantity);
                // Refresh cart after qty update
                await refreshCart(response);
            } catch (error) {
                if (error instanceof ApiClientError) {
                    const errors = resolveApiErrors(error.details.errors);
                    errors.forEach((error) => hfpPushError(error));
                }
            }
        },
        onCommitted: (settledQuantity) => {
            getErrorsCodes()?.forEach((element) => {
                hfpPushError(
                    t(`errors.${element.messageKey}`, {
                        ...element,
                        quantity: settledQuantity,
                        name: cartItem.label,
                        stock: settledQuantity
                    })
                );
            });
        }
    });

    const isLoading = computed(() => isUpdating.value || isRemoving.value);

    /** Anything pending on this item, or any Shopware write in flight. */
    const isItemBusy = computed(() => isLoading.value || isQuantityEditPending.value || isWritingToShopware.value);

    const removeCartItem = async () => {
        // Guards a double-click, and a removal racing a debounced quantity edit
        // that cannot be cancelled once scheduled.
        if (isItemBusy.value) return;

        isRemoving.value = true;

        try {
            await removeItem();
        } finally {
            isRemoving.value = false;
        }
    };
</script>

<template>
    <div
        v-if="!isPromotion"
        class="cart-item"
        :class="{
            '--checkout': isCheckout,
            '--promotion': isPromotion,
            '--has-options': itemOptions
        }"
    >
        <RouterLink
            :to="seoLink?.link ? useStoryblokUrl(seoLink?.link) : getProductRoute(cartItem)"
            class="product-link"
            data-testid="product-box"
        >
            <div class="image-wrapper">
                <ShopwarePicture
                    v-if="cartItem.cover?.url"
                    :media="cartItem.cover"
                    :alt="cartItem.label || cartItem.payload.name"
                    type="thumbnail"
                    data-testid="cart-product-image"
                />

                <img
                    v-else
                    src="@/assets/images/no-image.jpg"
                    alt="Kein Bild gepflegt"
                />
            </div>
        </RouterLink>

        <div
            class="info-container"
            :class="{ '--has-discount': listPrice }"
        >
            <RouterLink
                :to="seoLink?.link ? useStoryblokUrl(seoLink?.link) : getProductRoute(cartItem)"
                class="product-link"
                data-testid="product-box"
            >
                <div>
                    <div class="item-header">
                        <h3
                            class="product-name"
                            data-testid="cart-product-name"
                        >
                            {{ cartItem.label }}
                        </h3>
                        <SharedPrice
                            v-if="itemTotalPrice"
                            :value="itemTotalPrice"
                            data-testid="cart-product-price"
                        >
                            <template
                                v-if="listPrice"
                                #afterPrice
                            >
                                <div class="discount">
                                    <p>statt</p>
                                    <span>{{ listPrice }}</span>
                                </div>
                            </template>
                        </SharedPrice>
                    </div>

                    <p
                        v-if="itemOptions"
                        data-testid="cart-product-options"
                        class="options"
                    >
                        <span
                            v-for="option in itemOptions"
                            :key="option.group"
                        >
                            {{ option.group }}: {{ option.option }}
                        </span>
                    </p>
                </div>
            </RouterLink>

            <div
                v-if="!isPromotion"
                class="user-actions"
            >
                <input
                    v-model="quantity"
                    type="number"
                    :disabled="isLoading"
                    :min="1"
                    :max="itemStock || maxQty"
                    :step="1"
                    data-testid="cart-product-qty-select"
                    name="quantity"
                    aria-label="Einkaufswagen Anzahl"
                    class="quantity-input"
                />
                <div class="flex">
                    <button
                        v-if="!isPromotion"
                        type="button"
                        class="delete"
                        :disabled="isItemBusy"
                        data-testid="product-remove-button"
                        @click="removeCartItem"
                    >
                        <span class="aria-hidden">Produkt entfernen</span>

                        <img
                            src="~/assets/icons/trash.svg"
                            alt="löschen"
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div
        v-else
        class="cart-item"
        :class="{ '--promotion': isPromotion, '--side-cart': isSideCart }"
    >
        <h3
            class="product-name"
            data-testid="cart-product-name"
        >
            {{ cartItem.label }}
        </h3>
        <SharedPrice
            v-if="itemTotalPrice"
            :value="itemTotalPrice"
            data-testid="cart-product-price"
        >
            <template
                v-if="listPrice"
                #afterPrice
            >
                <div class="discount">
                    <p>statt</p>
                    <span>{{ listPrice }}</span>
                </div>
            </template>
        </SharedPrice>
    </div>
</template>

<style scoped>
    @import '~/assets/scss/cart/cart-item.scss';
</style>
